import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";

import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import {
  Card,
  CardContent,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { PricingFoundation } from "@/components/marketing/solution/PricingFoundation";
import { SolutionCta } from "@/components/marketing/solution/SolutionCta";
import {
  CONTACT_SOURCE_TAGS,
  PRIMARY_CTA_LABEL,
  QUALIFIER_PATH,
} from "@/components/marketing/solution/constants";
import {
  DAYS_TO_OUTCOME,
  IMPLEMENTATION_FROM,
  formatRand,
} from "@/components/marketing/solution/pricing";

export const Route = createFileRoute("/pricing")({
  component: PricingNewPage,
});

const FAQ = [
  {
    question: "Do you charge per user?",
    answer:
      "No. Larasoft has no per-user fees, ever. Your plan covers your whole organisation - invite everyone who needs answers without a per-seat decision.",
  },
  {
    question: "Is there an implementation cost?",
    answer: `Only if you bring your own team. Engage our fractional AI and automation team and getting connected is part of that, from R30 000 a month. Prefer to use your own team? We do the initial implementation from ${formatRand(IMPLEMENTATION_FROM)}, working alongside them.`,
  },
  {
    question: "What happens as we grow?",
    answer:
      "Bring in the fractional team whenever you are ready to accelerate, or add to the platform as your goals change. There are no lock-in penalties for moving up.",
  },
  {
    question: "Do you offer special pricing for nonprofits?",
    answer:
      "Yes. We are passionate about empowering NGOs and nonprofit organisations, and special pricing is available so nonprofits can access the same enterprise-grade tools. Tell us about your organisation and we will work it out with you.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

function PricingNewPage() {
  return (
    <MarketingLayout>
      <Helmet>
        <title>Pricing | Larasoft</title>
        <link rel="canonical" href="https://larasoft.global/pricing" />
        <meta
          name="description"
          content="Larasoft pricing - we bring the technology and the expertise to make your organisation smarter, sharper and more automated. Subscription from R10 000 a month."
        />
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      {/* Hero */}
      <Section
        containerClassName="max-w-4xl space-y-8"
        className="py-16 sm:py-20"
      >
        <div className="space-y-5">
          <h1 className="text-3xl font-extrabold leading-tight text-brand-blue-dark sm:text-4xl md:text-5xl">
            What would a smarter, sharper, more automated organisation look
            like, in as soon as {DAYS_TO_OUTCOME} days?
          </h1>
          <p className="max-w-2xl text-lg text-slate-600">
            More organisations are turning to AI and data to improve how they
            run: sharper decisions, more automation, more predictable
            outcomes. We bring both the technology and the expertise to get
            you there, embedded in your team, measured against what it
            returns.
          </p>
        </div>
      </Section>

      {/* Pricing */}
      <Section containerClassName="space-y-8" className="py-8 sm:py-12">
        <PricingFoundation />
        <p className="text-center text-sm text-slate-500">
          Prices in South African rand for South African organisations, and
          in US dollars for the rest of the world, excluding VAT, billed
          monthly unless stated once off.
        </p>
      </Section>

      {/* Not sure where you fit - route to the qualifier */}
      <Section containerClassName="max-w-3xl" className="py-8">
        <Card className="border border-slate-200 bg-slate-50">
          <CardContent className="flex flex-col items-start gap-4 space-y-0 p-8 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <CardTitle className="text-lg">
                Not sure where you fit?
              </CardTitle>
              <CardDescription>
                Five questions, under two minutes - and you will know whether
                you qualify for a free proof of concept on your own data.
              </CardDescription>
            </div>
            <SolutionCta
              to={QUALIFIER_PATH}
              event="hero_secondary_cta"
              eventDetail="pricing"
              variant="outline"
              className="flex-none"
            >
              See if you qualify
            </SolutionCta>
          </CardContent>
        </Card>
      </Section>

      {/* FAQ */}
      <Section
        id="faq"
        containerClassName="max-w-3xl space-y-8"
        className="py-12 sm:py-16"
      >
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-brand-blue-dark sm:text-3xl">
            Frequently asked questions
          </h2>
          <p className="text-sm text-slate-600">
            How Larasoft is priced, and what happens as you grow.
          </p>
        </div>
        <div className="space-y-4">
          {FAQ.map((item) => (
            <Card key={item.question}>
              <CardContent className="space-y-2 p-6">
                <CardTitle className="text-base">{item.question}</CardTitle>
                <CardDescription>{item.answer}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Closing CTA */}
      <Section
        containerClassName="space-y-6 text-center"
        className="bg-gradient-to-r from-brand-blue-dark via-brand-blue-medium to-brand-blue-dark py-16 text-white"
      >
        <h2 className="text-2xl font-bold sm:text-3xl">
          Let us size it with you.
        </h2>
        <p className="mx-auto max-w-xl text-white/85">
          Tell us which systems you run, where it hurts most, and what you
          want to automate. We will tell you what it takes to get there, and
          whether you qualify for a free proof of concept.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <SolutionCta
            event="final_cta"
            eventDetail="pricing"
            search={{ source: CONTACT_SOURCE_TAGS.pricing }}
            className="bg-white text-brand-blue-dark hover:bg-white/90"
          >
            {PRIMARY_CTA_LABEL}
          </SolutionCta>
        </div>
      </Section>
    </MarketingLayout>
  );
}
